import React from 'react';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import Popover from '@material-ui/core/Popover';
import { makeStyles } from '@material-ui/styles';
import Select, { createFilter } from 'react-select';
import { confirmAlert } from 'react-confirm-alert';
import Tooltip from '@material-ui/core/Tooltip';
import Hidden from '@material-ui/core/Hidden';
import LinearProgress from '@material-ui/core/LinearProgress';
import { useRootStore } from '../../../../mobx/bridge';
import UseWindowSize from '../UseWindowSize/UseWindowSize';
import styles from './LeftSideBar.module.css';
import SearchBar from '../SearchBar/SearchBar';
import InfiniteChannelList from '../InfiniteChannelList/InfiniteChannelList';
import SearchResultList from '../SearchResultList/SearchResultList';
import * as utils from '../../../../helpers/utils';
import CustomConfirmAlert from '../CustomConfirmAlert/CustomConfirmAlert';
import commonStyles from '../../../../assets/stylesheets/common.module.css';
import CustomBrowserPicker from '../CustomBrowserPicker/CustomBrowserPicker';

const stringify = (option) => option.label;
const filterOption = createFilter({ stringify });

const anchorOrigin = {
  vertical: 'bottom',
  horizontal: 'right',
};

const transformOrigin = {
  vertical: 'top',
  horizontal: 'right',
};

const bubbleMenu = makeStyles({
  root: {
    display: 'flex',
    // width: '154px',
    margin: '10px 0 0 0',
    background: '#ffffff',
    border: '1px solid #D0D5E1',
    borderRadius: '3',
    boxShadow: '0px 0px 6px #0000001A',
    overflow: 'visible',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: '0',
      right: '9px',
      zIndex: '1001',
      width: '0',
      height: '0',
      border: '7px solid transparent',
      borderBottomColor: '#ffffff',
      borderTop: '0',
      marginLeft: '-7px',
      marginTop: '-7px',
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      top: '0',
      right: '8px',
      zIndex: '1000',
      width: '0',
      height: '0',
      border: '8px solid transparent',
      borderBottomColor: '#dfdfdf',
      borderTop: '0',
      marginLeft: '-8px',
      marginTop: '-8px',
    },
  },
});

const customStyles = {
  container: (provided) => ({
    ...provided,
    background: '#f5f5f5',
    // marginLeft: '20px',
    '@media (max-width: 600px)': {
      margin: '0 auto',
    },
    color: '#5e5e5e',
    border: 'none',
    borderRadius: '18px',
    // borderBottom: '1px solid #FFFFFF',
  }),
  control: (provided) => ({
    ...provided,
    boxShadow: 'none',
    position: 'relative',
    minWidth: '320px',
    '@media (max-width: 400px)': {
      minWidth: '270px',
    },
    '@media (max-width: 320px)': {
      minWidth: '220px',
    },
    height: '36px',
    minHeight: '36px',
    background: 'none',
    border: 'none !important',
    borderRadius: '0px !important',
    cursor: 'pointer',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    // height: '20px'
  }),
  indicatorSeparator: () => ({
    display: 'none !imporant',
  }),
  singleValue: (provided) => ({
    ...provided,
    font: '16px Roboto, serif',
    color: '#5e5e5e',
    height: '20',
  }),
  input: () => ({
    font: '14px Arial,serif',
    color: '#5e5e5e',
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '20px',
    // overflow: 'unset',
    padding: '0 17px',
  }),
  menu: (provided) => ({
    ...provided,
    font: '14px Arial, serif',
  }),
  dropdownIndicator: () => ({
    paddingTop: '0',
    paddingBottom: '0',
    paddingRight: '7px',
    color: '#384348',
  }),
  option: (provided, { data }) => ({
    ...provided,
    '&:after': {
      content: data?.value?.hasUnseenMessages ? '"\u25cf"' : null,
      fontSize: '18px',
      color: 'red',
      margin: '0 0 0 10px',
    },
  }),
};

const channelListLoadingBar = {
  root: styles.channelListLoadingBar,
  barColorPrimary: styles.barColorPrimary,
  colorPrimary: styles.colorPrimary,
};

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const LeftSideBar = observer(function LeftSideBar() {
  const {
    commonStore,
    sendingEvents,
    settingsStore,
    messageStore,
    channelStore,
    contactTypeStore,
    channelTypeStore,
  } = useRootStore();
  const [, height] = UseWindowSize();
  const { t } = useTranslation();

  const [moreOptions, setMoreOptions] = React.useState(null);

  const [channelsFilter, setChannelsFilter] = React.useState(null);

  const [selectedOption, setSelectedOption] = React.useState(
    commonStore.toolbarDropdownLocations.find(
      (f) =>
        f.value.CPLocationID.toString() === commonStore.selectedLocation.CPLocationID.toString()
    )
  );

  const settings = settingsStore.getLoggedUserSettings;

  const displayWebChats = commonStore.channelTypeIdsToSearch.includes(
    utils.ChannelTypeEnum.WebChat.TypeId
  );

  const displaySmsChannels = commonStore.channelTypeIdsToSearch.includes(
    utils.ChannelTypeEnum.SMS.TypeId
  );

  const numOfUnseenActiveWebChatMessages =
    commonStore.getHasAccessToWebChats &&
    !commonStore.showActiveWebChatChannels &&
    messageStore.computedGetUnseenMessages(
      null,
      [utils.ChannelTypeEnum.WebChat.TypeId],
      [utils.ChannelStateTypeEnum.Active.TypeId]
    ).length;

  const numOfUnseenClosedWebChatMessages =
    commonStore.getHasAccessToWebChats &&
    !commonStore.showClosedWebChatChannels &&
    messageStore.computedGetUnseenMessages(
      null,
      [utils.ChannelTypeEnum.WebChat.TypeId],
      [utils.ChannelStateTypeEnum.Closed.TypeId]
    ).length;

  const numOfUnseenTextChatMessages =
    commonStore.getHasAccessToTextChats &&
    !displaySmsChannels &&
    messageStore.computedGetUnseenMessages(null, [utils.ChannelTypeEnum.SMS.TypeId], null).length;

  const hasLocationPicker = commonStore.locations && commonStore.locations?.length > 1;

  commonStore.leftSideBarListContainerRef = React.useRef(null);

  const isMoreOptionsOpen = Boolean(moreOptions);
  const isChannelsFilterOpen = Boolean(channelsFilter);
  const popperClass = bubbleMenu();

  const handleRef = (incRef) => {
    if (incRef) {
      commonStore.leftSideBarListContainerRef.current = incRef;
    }
  };

  const containerHeight = () => {
    const heightToDeduct = window.innerWidth > 959 ? 117 : 148;

    return height
      ? height -
          (commonStore.cpHeaderHeight || 0) -
          heightToDeduct -
          (hasLocationPicker ? 43 : 0) -
          (commonStore.getHasOnlyWebChatSearchResults ? 153 : 0)
      : 0;
  };

  const createCustomerChannel = debounce(() => {
    const customerPhoneNumber = commonStore.channelSearchInput.replace(/[^\d]/g, '');
    const formattedNumber = utils.formatPhoneNumber(customerPhoneNumber);

    const phoneNumber = {
      _id: utils.generateGuid(),
      Number: formattedNumber,
      TwilioFormat: `+1${customerPhoneNumber}`,
    };

    const customer = {
      _id: utils.generateGuid(),
      ContactType: contactTypeStore.computedGetByTypeId(utils.ContactTypeEnum.Customer.TypeId),
      PhoneNumbers: [phoneNumber],
    };

    const channel = {
      _id: utils.generateGuid(),
      ChannelName: t('Common.TextMessageWithContact', {
        contactName: formattedNumber,
      }),
      ChannelType: channelTypeStore.computedGetByTypeId(utils.ChannelTypeEnum.SMS.TypeId),
      Contacts: [commonStore.getLoggedUser, customer],
      CustomerPhoneNumber: phoneNumber,
    };

    sendingEvents.socketEmitRoom(
      channel,
      utils.maxNumberOfItemsByContainerSize(document, commonStore.messagesContainerRef)
    );
    commonStore.setChannelSearchInput('');
  }, 250);

  const toggleMuteChatSounds = debounce(() => {
    sendingEvents.socketEmitToggleMuteChatSounds();
  }, 250);

  const showOptions = (e) => {
    setMoreOptions(e.currentTarget);
  };

  const closeShowOptions = () => {
    if (moreOptions) {
      setMoreOptions(null);
    }
  };

  const showChannelsFilter = (e) => {
    setChannelsFilter(e.currentTarget);
  };

  const closeChannelsFilter = () => {
    if (channelsFilter) {
      setChannelsFilter(null);
    }
  };

  const openSettings = () => {
    closeShowOptions();
    commonStore.setToggleSettingsMenu();
  };

  const onLocationChangedHandler = (option) => {
    setSelectedOption(option);
    commonStore.setChangeLocation(option.value);
  };

  const showBrowserPickerAlert = () => {
    commonStore.setCustomModalTitle(t('CustomBrowserPicker.FindHowToEnableNotifications'));

    confirmAlert({
      customUI: ({ onClose }) => (
        <CustomConfirmAlert
          customComponent={<CustomBrowserPicker />}
          falseText={t('Common.Close')}
          falseEvent={onClose}
          customStyles={{
            display: 'table-cell',
          }}
          commonStore={commonStore}
          additionalStyles={{
            customAlertTitle: {
              font: 'bold 34px Roboto, serif',
              color: '#384348',
              border: 'none',
            },
            customAlertButton: {
              color: '#1473E6',
              font: 'bold 16px Roboto, serif',
              textDecoration: 'underline',
            },
          }}
        />
      ),
    });
  };

  const clearAllNotifications = debounce(() => {
    messageStore.setClearAllNotifications();
    sendingEvents.socketEmitClearAllNotifications();
    closeShowOptions();
  }, 250);

  const confirmClearAllNotifications = () => {
    closeShowOptions();
    confirmAlert({
      customUI: ({ onClose }) => (
        <CustomConfirmAlert
          title={t('Common.Warning')}
          message={t('LeftSideBar.MarkAllConversationsAsReadWarningMessage')}
          trueText={t('Common.Confirm')}
          trueEvent={clearAllNotifications}
          falseText={t('Common.Close')}
          falseEvent={onClose}
        />
      ),
    });
  };

  const markChannelAsUnread = debounce(() => {
    if (settings?.SelectedChannel) {
      sendingEvents.socketEmitMarkChannelAsUnread(settings.SelectedChannel._id);
    }
    closeShowOptions();
  }, 250);

  const checkForMoreChannels = () => {
    commonStore.setLocationHasMoreChannels(true);

    if (channelStore.getChannels?.length === 0) {
      commonStore.loadMoreChannels();
    }
  };

  const toggleDisplaySMSChannels = debounce(() => {
    commonStore.setToggleDisplaySMSSearchTypes();
    checkForMoreChannels();
  }, 250);

  const toggleDisplayWebchatChannels = debounce(() => {
    commonStore.setToggleDisplayWebchatSearchTypes();
    checkForMoreChannels();
  }, 250);

  const toggleDisplayActiveWebchatChannels = debounce(() => {
    commonStore.setToggleDisplayActiveWebchatSearchTypes();
    checkForMoreChannels();
  }, 250);

  const toggleDisplayClosedWebchatChannels = debounce(() => {
    commonStore.setToggleDisplayClosedWebchatSearchTypes();
    checkForMoreChannels();
  }, 250);

  const toggleDisplayUnreadChannelsFilter = debounce(() => {
    commonStore.setToggleDisplayUnreadChannelFilter();
    checkForMoreChannels();
  }, 250);

  const renderNotificationsBlockedAlert = () => (
    <Hidden smDown>
      <button
        type="button"
        className={styles.notificationsBlockedBtn}
        onClick={showBrowserPickerAlert}
      >
        <span className={styles.blockedSoundBtnIcon} />

        {t('LeftSideBar.NotificationsBlocked')}

        <Tooltip arrow enterDelay={1000} title={t('LeftSideBar.LearnHowToEnableNotifications')}>
          <span className={styles.moreInfoIcon} />
        </Tooltip>
      </button>
    </Hidden>
  );

  const renderMuteSoundButton = () => (
    <Tooltip
      arrow
      enterDelay={1000}
      title={
        settings?.MuteSounds || !commonStore.audioContext
          ? t('LeftSideBar.UnmuteSound')
          : t('LeftSideBar.MuteSound')
      }
    >
      <button
        type="button"
        className={[
          styles.toggleMuteSoundsBtn,
          settings?.MuteSounds || !commonStore.audioContext
            ? styles.unmuteSoundsBtn
            : styles.muteSoundsBtn,
        ].join(' ')}
        onClick={toggleMuteChatSounds}
      />
    </Tooltip>
  );

  const renderSoundsButton = () => {
    if (commonStore.notificationsBlocked) {
      return renderNotificationsBlockedAlert();
    }

    if (settings) {
      return renderMuteSoundButton();
    }

    return null;
  };

  const leftSideBarContainerStyles = commonStore.getHasOnlyWebChatSearchResults
    ? [styles.leftSideBarListContainer, styles.leftSideBarListContainerOnylWebChatResults].join(' ')
    : styles.leftSideBarListContainer;

  return (
    <Grid component="div" container>
      <Grid
        component="div"
        container
        justify="center"
        alignItems="center"
        align="center"
        item
        xs={12}
        className={styles.channelListTopContainer}
      >
        <Grid
          container
          component="div"
          item
          xs={12}
          className={styles.channelListTopInnerContainer}
        >
          <div>
            <span className={styles.title}>{t('LeftSideBar.ChatsTitle')}</span>
          </div>

          <div className={styles.topBtnsContainer}>
            {renderSoundsButton()}

            <Tooltip arrow enterDelay={1000} title={t('LeftSideBar.MoreOptions')}>
              <button
                type="button"
                className={styles.moreOptionsBtn}
                onClick={showOptions}
                data-testid="moreOptionsBtn"
                id="moreOptionsBtn"
              />
            </Tooltip>

            <Popover
              open={isMoreOptionsOpen}
              anchorEl={moreOptions}
              onClose={closeShowOptions}
              anchorOrigin={anchorOrigin}
              transformOrigin={transformOrigin}
              classes={{ paper: popperClass.root }}
            >
              <Grid component="div" container className={styles.optionsContainer}>
                <Grid component="div" item xs={12} className={styles.topOptions}>
                  <button
                    type="button"
                    onClick={confirmClearAllNotifications}
                    className={styles.option}
                  >
                    {t('LeftSideBar.ClearAllNotifications')}
                  </button>

                  <button type="button" onClick={markChannelAsUnread} className={styles.option}>
                    {t('LeftSideBar.MarkConversationAsUnread')}
                  </button>

                  <div className={styles.separator} />

                  <button
                    type="button"
                    onClick={openSettings}
                    className={styles.option}
                    data-testid="settingsBtn"
                  >
                    <span className={styles.settingsIcon} />

                    {t('LeftSideBar.Settings')}
                  </button>
                </Grid>
              </Grid>
            </Popover>
          </div>
        </Grid>

        <Grid component="div" item xs={12}>
          {hasLocationPicker ? (
            <div className={styles.locationPickerContainer}>
              <Select
                styles={customStyles}
                classNamePrefix="react-select"
                filterOption={filterOption}
                value={selectedOption}
                onChange={onLocationChangedHandler}
                options={commonStore.toolbarDropdownLocations}
                blurInputOnSelect
                className="react-select-container"
                id="leftSideLocationPicker"
              />

              {commonStore.locations.some((s) => s.hasUnseenMessages) ? (
                <span className={styles.locationPickerNotificationBadge} />
              ) : null}
            </div>
          ) : null}
        </Grid>

        <Grid container component="div" item xs={12}>
          <div className={styles.searchBarContainer}>
            <SearchBar />
          </div>

          <div>
            <Tooltip arrow enterDelay={1000} title={t('LeftSideBar.FilterChannelTypes')}>
              <button
                type="button"
                className={styles.channelsFilterBtn}
                onClick={showChannelsFilter}
              >
                {numOfUnseenTextChatMessages ||
                numOfUnseenActiveWebChatMessages ||
                numOfUnseenClosedWebChatMessages ? (
                  <span
                    className={[styles.notificationBadge, styles.channelsFilterBtnBadge].join(' ')}
                  />
                ) : null}
              </button>
            </Tooltip>
          </div>

          <Popover
            open={isChannelsFilterOpen}
            anchorEl={channelsFilter}
            onClose={closeChannelsFilter}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            classes={{ paper: popperClass.root }}
          >
            <div className={styles.optionsContainer}>
              <button type="button" className={[styles.option, styles.titleOption].join(' ')}>
                {t('LeftSideBar.Display')}
              </button>

              <div className={styles.separator} />

              <button type="button" className={styles.option}>
                <label htmlFor="selectUnreadFilter" className={styles.checkBoxContainer}>
                  <input
                    type="checkbox"
                    id="selectUnreadFilter"
                    className={commonStyles.settingsCheckBox}
                    checked={commonStore.showUnreadChannelFilter}
                    onChange={toggleDisplayUnreadChannelsFilter}
                  />

                  <span className={[commonStyles.checkMark, styles.checkbox].join(' ')} />

                  <span className={[styles.chatIcon, styles.unreadFilterIcon].join(' ')} />

                  <span>{t('LeftSideBar.UnreadFilter')}</span>
                </label>
              </button>

              {commonStore.getHasAccessToWebChats ? (
                <>
                  {commonStore.availableModes?.length > 1 ? (
                    <button type="button" className={styles.option}>
                      <label htmlFor="selectWebChats" className={styles.checkBoxContainer}>
                        <input
                          type="checkbox"
                          id="selectWebChats"
                          className={commonStyles.settingsCheckBox}
                          checked={displayWebChats}
                          onChange={toggleDisplayWebchatChannels}
                        />

                        <span className={[commonStyles.checkMark, styles.checkbox].join(' ')} />

                        <span className={[styles.chatIcon, styles.webChatIcon].join(' ')} />

                        <span>{t('LeftSideBar.WebsiteChats')}</span>
                      </label>
                    </button>
                  ) : null}

                  <button
                    type="button"
                    className={[
                      styles.option,
                      commonStore.availableModes?.length > 1 ? styles.nested : '',
                    ].join(' ')}
                  >
                    {numOfUnseenActiveWebChatMessages ? (
                      <span className={styles.notificationBadge} />
                    ) : null}

                    <label htmlFor="selectActiveWebChats" className={styles.checkBoxContainer}>
                      <input
                        type="checkbox"
                        id="selectActiveWebChats"
                        className={commonStyles.settingsCheckBox}
                        checked={displayWebChats && commonStore.showActiveWebChatChannels}
                        onChange={toggleDisplayActiveWebchatChannels}
                      />

                      <span className={[commonStyles.checkMark, styles.checkbox].join(' ')} />

                      <span className={[styles.chatIcon, styles.webChatIcon].join(' ')} />

                      <span>{t('LeftSideBar.WebsiteChatsActive')}</span>
                    </label>
                  </button>

                  <button
                    type="button"
                    className={[
                      styles.option,
                      commonStore.availableModes?.length > 1 ? styles.nested : '',
                    ].join(' ')}
                  >
                    {numOfUnseenClosedWebChatMessages ? (
                      <span className={styles.notificationBadge} />
                    ) : null}

                    <label htmlFor="selectClosedWebChats" className={styles.checkBoxContainer}>
                      <input
                        type="checkbox"
                        id="selectClosedWebChats"
                        className={commonStyles.settingsCheckBox}
                        checked={displayWebChats && commonStore.showClosedWebChatChannels}
                        onChange={toggleDisplayClosedWebchatChannels}
                      />

                      <span className={[commonStyles.checkMark, styles.checkbox].join(' ')} />

                      <span className={[styles.chatIcon, styles.webChatClosedIcon].join(' ')} />

                      <span>{t('LeftSideBar.WebsiteChatsClosed')}</span>
                    </label>
                  </button>
                </>
              ) : null}

              {commonStore.availableModes?.length > 1 && commonStore.getHasAccessToTextChats ? (
                <button type="button" className={styles.option}>
                  {numOfUnseenTextChatMessages ? (
                    <span className={styles.notificationBadge} />
                  ) : null}

                  <label htmlFor="selectTextMessages" className={styles.checkBoxContainer}>
                    <input
                      type="checkbox"
                      id="selectTextMessages"
                      className={commonStyles.settingsCheckBox}
                      checked={displaySmsChannels}
                      onChange={toggleDisplaySMSChannels}
                    />

                    <span className={[commonStyles.checkMark, styles.checkbox].join(' ')} />

                    <span className={[styles.chatIcon, styles.textChatIcon].join(' ')} />

                    <span>{t('LeftSideBar.TextChats')}</span>
                  </label>
                </button>
              ) : null}
            </div>
          </Popover>
        </Grid>
      </Grid>

      <Grid component="div" item xs={12}>
        {commonStore.getHasAccessToTextChats &&
        commonStore.channelSearchInput &&
        (commonStore.getSearchResultsFilteredByType.length === 0 ||
          commonStore.getHasOnlyWebChatSearchResults) &&
        !commonStore.isFetchingSearchResults &&
        utils.isValidPhoneNumber(commonStore.channelSearchInput) ? (
          <Grid component="div" item xs={12} className={styles.addNewContact}>
            <div className={styles.addContactBtnContainer}>
              <button
                type="button"
                className={styles.addContactBtn}
                onClick={createCustomerChannel}
              >
                <Grid component="div" container alignItems="center">
                  <Grid item xs={2}>
                    <span className={styles.addContactBtnPlus}>+</span>
                  </Grid>

                  <Grid item xs={10}>
                    {t('LeftSideBar.TextPhoneNumber', {
                      phoneNumber: utils.formatPhoneNumber(commonStore.channelSearchInput),
                    })}
                  </Grid>
                </Grid>
              </button>
            </div>

            <Grid component="div" container justify="center" alignItems="center" align="center">
              <div className={styles.learnMoreTextBlock}>{t('LeftSideBar.TextInfoMessage')}</div>
            </Grid>
          </Grid>
        ) : null}

        <div
          ref={handleRef}
          className={
            commonStore.channelSearchInput &&
            commonStore.getSearchResultsFilteredByType.length === 0
              ? styles.leftSideBarListContainerNoResults
              : leftSideBarContainerStyles
          }
        >
          {commonStore.channelSearchInput ? (
            <SearchResultList
              listHeight={containerHeight()}
              collectionToShow={commonStore.getSearchResultsFilteredByType}
            />
          ) : (
            <InfiniteChannelList listHeight={containerHeight()} />
          )}
          {commonStore.isWaitingForLoadMoreChannels ? (
            <LinearProgress classes={channelListLoadingBar} />
          ) : null}
        </div>
      </Grid>
    </Grid>
  );
});
LeftSideBar.whyDidYouRender = true;
LeftSideBar.displayName = 'LeftSideBar';

export default React.memo(LeftSideBar);
